import type { GridFilterModel, GridSortModel } from '@mui/x-data-grid-premium';
import { format } from 'date-fns';

import type { CompliancesListOrderingItem, CompliancesListParams } from '~/data/generated/model';

export type BuildFilterParamsOpts = {
  page: number;
  pageSize: number;
  filterModel: GridFilterModel;
  sortModel: GridSortModel;
  searchParams: URLSearchParams;
};

const fieldToApiMapping: Record<string, string> = {
  unitName: 'unit_name',
  ahjId: 'ahj_id',
  jurisdiction: 'jurisdiction',
  dateIssued: 'date_issued',
  certificateExpiration: 'expiration_date',
  expirationDate: 'expiration_date',
  // UK "Next Examination" — the latest LOLER report's next examination date,
  // which the API exposes as its own ordering/filter field. (CX-416)
  nextInspectionDue: 'next_inspection_due',
  // UK "Last Examination" — the latest LOLER report's own inspection date,
  // filterable/sortable under its own name for the same reason as
  // nextInspectionDue above. Without this mapping, `getApiFieldName` falls
  // through to the bare field name and sends `ordering=examinationDate`,
  // which the API's OrderingFilter rejects, 400ing the whole grid load
  // (AMP-687).
  examinationDate: 'examination_date',
  property: 'property',
  portfolio: 'portfolio',
  serviceProvider: 'service_provider',
  region: 'region',
  inspectionStatus: 'inspection_status',
  nextViolationDue: 'next_violation_due',
  hasStaleViolations: 'has_stale_violations',
  openViolations: 'has_open_violations',
  closedViolations: 'has_closed_violations',
  violationsWithoutUpdates: 'violations_without_updates',
  lastCat1Test: 'last_cat1_test',
  lastCat5Test: 'last_cat5_test',
  __row_group_by_columns_group__: 'portfolio',
};
type Field = keyof typeof fieldToApiMapping;

const getApiFieldName = (field: Field): string => {
  return fieldToApiMapping[field] || field;
};

/**
 * Column field -> `ComplianceFilter.ordering` choice.
 *
 * Separate from `fieldToApiMapping` because the endpoint names the same thing
 * differently on each side: `portfolio`/`property`/`service_provider` are the
 * *filter* params (ids), while ordering registers `portfolio_name`,
 * `property_name` and `service_provider_name`. Sorting those three columns sent
 * the filter name and 400'd the whole grid load -- `OrderingFilter` is a
 * `ChoiceFilter`, and `DjangoFilterBackend` (`raise_exception = True`) turns an
 * unrecognized choice into a 400 rather than a silent no-sort.
 *
 * A column missing from this map is not sortable server-side, so its sort is
 * dropped instead of guessed at: sending a bare camelCase field name is the same
 * 400 (AMP-687 hit it with `examinationDate`).
 */
const fieldToOrderingName: Record<string, CompliancesListOrderingItem> = {
  unitName: 'unit_name',
  portfolio: 'portfolio_name',
  property: 'property_name',
  ahjId: 'ahj_id',
  serviceProvider: 'service_provider_name',
  jurisdiction: 'jurisdiction',
  certificateExpiration: 'expiration_date',
  expirationDate: 'expiration_date',
  nextInspectionDue: 'next_inspection_due',
  examinationDate: 'examination_date',
  inspectionStatus: 'inspection_status',
  openViolations: 'has_open_violations',
  closedViolations: 'has_closed_violations',
  nextViolationDue: 'next_violation_due',
  violationsWithoutUpdates: 'violations_without_updates',
  lastCat1Test: 'last_cat1_test',
  lastCat5Test: 'last_cat5_test',
  // The grouping column stands in for the field it groups by.
  __row_group_by_columns_group__: 'portfolio_name',
};

/**
 * The compliance query, as the endpoint takes it.
 *
 * Pinned to the generated params so a value whose shape disagrees with the schema
 * fails `tsc` here rather than going out on the wire and matching nothing — which
 * is what an array sent to a single-valued `region` did. The open index signature
 * is for `default:` below, which writes keys off a runtime map that has no
 * compile-time shape.
 */
type ComplianceFilterParams = CompliancesListParams & Record<string, unknown>;

export const buildFilterParams = ({
  page,
  pageSize,
  filterModel,
  sortModel,
  searchParams,
}: BuildFilterParamsOpts): ComplianceFilterParams => {
  const params: ComplianceFilterParams = {
    limit: pageSize,
    offset: page * pageSize,
  };

  const ordering = sortModel
    .filter((item) => fieldToOrderingName[item.field] !== undefined)
    // Every value in `fieldToOrderingName` is a registered choice and so is its
    // descending twin, which is what makes the prefixed string one too.
    .map(
      (item) =>
        `${item.sort === 'desc' ? '-' : ''}${fieldToOrderingName[item.field]}` as CompliancesListOrderingItem,
    );
  if (ordering.length > 0) {
    // Documented as a non-exploding array, so the SDK joins it with commas — the
    // same string this used to build by hand.
    params.ordering = ordering;
  }

  if (filterModel.quickFilterValues?.length) {
    params.search = filterModel.quickFilterValues.join(' ');
  }

  filterModel.items.forEach((item) => {
    if (!item.value && item.value !== false) return;
    const apiFieldName = getApiFieldName(item.field);

    switch (item.field) {
      case 'inspectionStatus':
        params.inspection_status = item.value;
        break;

      case 'nextViolationDue':
        if (item.operator === 'onOrAfter') {
          params.next_violation_due_after = format(new Date(item.value as string), 'yyyy-MM-dd');
        } else if (item.operator === 'onOrBefore') {
          params.next_violation_due_before = format(new Date(item.value as string), 'yyyy-MM-dd');
        }
        break;

      case 'hasStaleViolations':
      case 'violationsWithoutUpdates':
        params.has_stale_violations = String(item.value) === 'true';
        break;

      case 'openViolations':
        params.has_open_violations = String(item.value) === 'true';
        break;

      case 'closedViolations':
        params.has_closed_violations = String(item.value) === 'true';
        break;

      case 'certificateExpiration':
        if (item.operator === 'onOrAfter') {
          params.expires_after = format(new Date(item.value as string), 'yyyy-MM-dd');
        } else if (item.operator === 'onOrBefore') {
          params.expires_before = format(new Date(item.value as string), 'yyyy-MM-dd');
        } else if (item.operator === 'equals') {
          const date = format(new Date(item.value as string), 'yyyy-MM-dd');
          params.expires_after = date;
          params.expires_before = date;
        }
        break;

      case 'nextInspectionDue':
        if (item.operator === 'onOrAfter') {
          params.next_inspection_due_after = format(new Date(item.value as string), 'yyyy-MM-dd');
        } else if (item.operator === 'onOrBefore') {
          params.next_inspection_due_before = format(new Date(item.value as string), 'yyyy-MM-dd');
        } else if (item.operator === 'equals') {
          const date = format(new Date(item.value as string), 'yyyy-MM-dd');
          params.next_inspection_due_after = date;
          params.next_inspection_due_before = date;
        }
        break;

      case 'examinationDate':
        if (item.operator === 'onOrAfter') {
          params.examination_date_after = format(new Date(item.value as string), 'yyyy-MM-dd');
        } else if (item.operator === 'onOrBefore') {
          params.examination_date_before = format(new Date(item.value as string), 'yyyy-MM-dd');
        } else if (item.operator === 'equals') {
          const date = format(new Date(item.value as string), 'yyyy-MM-dd');
          params.examination_date_after = date;
          params.examination_date_before = date;
        }
        break;

      case 'lastCat1Test':
        if (item.operator === 'onOrAfter') {
          params.last_cat1_test_after = format(new Date(item.value as string), 'yyyy-MM-dd');
        } else if (item.operator === 'onOrBefore') {
          params.last_cat1_test_before = format(new Date(item.value as string), 'yyyy-MM-dd');
        }
        break;

      case 'lastCat5Test':
        if (item.operator === 'onOrAfter') {
          params.last_cat5_test_after = format(new Date(item.value as string), 'yyyy-MM-dd');
        } else if (item.operator === 'onOrBefore') {
          params.last_cat5_test_before = format(new Date(item.value as string), 'yyyy-MM-dd');
        }
        break;
      default:
        if (apiFieldName) {
          params[apiFieldName] = item.value;
        }
    }
  });

  // Every selected value, not just the first: all three are multi-valued filters and
  // the global bar's controls are multi-select, so taking `[0]` narrowed the table to
  // one of the chosen portfolios while the chips still named them all.
  const portfolioIds = searchParams.getAll('portfolio');
  if (portfolioIds.length > 0) {
    params.portfolio = portfolioIds;
  }

  const serviceProviderIds = searchParams.getAll('service_provider');
  if (serviceProviderIds.length > 0) {
    params.service_provider = serviceProviderIds;
  }

  const regions = searchParams.getAll('region');
  if (regions.length > 0) {
    params.region = regions;
  }

  return params;
};
